import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
} from "@mui/material";
import { TAPI, TLocation, getLocation } from "../api.js";
import { TAsyncResult } from "../useAsyncValue.js";

import { TRoute } from "../types.js";
import { useTranslation } from "react-i18next";
import { RequestErrorToast } from "../ToastMessage.js";
import { DateTime } from "luxon";

type LoadedPageData = Awaited<ReturnType<typeof loadLocationStatus>>;

export function locationStatusTitle({
  dataResult,
}: {
  dataResult: TAsyncResult<LoadedPageData>;
  routeParams: { locationID: string };
}) {
  return dataResult.type === "RESOLVED" ? (
    <>{dataResult.value.name}</>
  ) : (
    <>...</>
  );
}

export async function loadLocationStatus(
  api: TAPI,
  { locationID, tenantKey }: { locationID: string; tenantKey: string },
) {
  return getLocation(api, tenantKey, locationID);
}

export function LocationStatusContent({
  dataResult: locationDataResult,
  routeParams,
}: {
  dataResult: TAsyncResult<LoadedPageData>;
  routeParams: { tenantKey: string; locationID: string };
}) {
  const { t } = useTranslation();

  if (locationDataResult.type === "NONE") {
    return null;
  }

  if (locationDataResult.type === "REJECTED") {
    return <RequestErrorToast requestError={locationDataResult.error} />;
  }

  if (locationDataResult.type === "PENDING") {
    return (
      <CircularProgress
        sx={{
          display: "block",
          margin: "auto",
          marginTop: 4,
          marginBottom: 4,
        }}
      />
    );
  }

  return (
    <TableContainer
      sx={{ margin: "50px auto", width: "90%" }}
      component={Paper}
    >
      <Table sx={{ minWidth: 650 }} aria-label="simple table">
        <TableHead>
          <TableRow>
            <TableCell>{t("location_status.reason")}</TableCell>
            <TableCell>{t("location_status.status")}</TableCell>
            <TableCell>{t("location_status.time")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {locationDataResult.value.connectionEvents?.map((row, key) => (
            <TableRow
              key={key}
              sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
            >
              <TableCell component="th" scope="row">
                {row.disconnectReason}
              </TableCell>
              <TableCell>{row.eventType}</TableCell>
              <TableCell>
                {DateTime.fromSeconds(row.timestamp).toLocaleString(
                  DateTime.DATETIME_SHORT_WITH_SECONDS,
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export const locationStatusRoute: TRoute<
  TLocation,
  { locationID: string; tenantKey: string }
> = {
  path: "/tenants/:tenantKey/location/:locationID/status",
  Title: locationStatusTitle,
  Content: LocationStatusContent,
  loadData: loadLocationStatus,
};
